/**
 * 이미지 압축 및 리사이징 유틸리티
 * 
 * 이미지를 업로드하기 전에 자동으로 압축하고 리사이즈하여
 * 서버 용량과 로딩 시간을 절약합니다.
 * 
 * 기본 설정:
 * - 일반 이미지: 최대 750x1500 비율 유지 축소
 * - 배너 이미지: 1450x485 cover crop
 * - 최대 파일 크기: 10MB
 * - 기본 품질: 0.8
 * - 기본 포맷: WebP (브라우저 미지원 시 JPEG로 폴백)
 */

const DEFAULT_COMPRESS_CONFIG = {
  mode: "contain",
  maxWidth: 750,
  maxHeight: 1500,
  maxFileSize: 10 * 1024 * 1024,
  initialQuality: 0.8,
  minQuality: 0.5,
  qualityStep: 0.1,
  outputMimeWebp: "image/webp",
  outputMimeJpeg: "image/jpeg",
  backgroundColor: "#ffffff",
  debugLabel: "기본 이미지",
};

const BANNER_COMPRESS_CONFIG = {
  ...DEFAULT_COMPRESS_CONFIG,
  mode: "cover",
  targetWidth: 1450,
  targetHeight: 485,
  debugLabel: "배너 이미지",
};

function loadImageElement(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        resolve(img);
      };

      img.onerror = () => {
        reject(new Error("이미지 로드에 실패했습니다."));
      };

      img.src = e.target.result;
    };

    reader.onerror = () => {
      reject(new Error("파일 읽기에 실패했습니다."));
    };

    reader.readAsDataURL(file);
  });
}

function calculateContainDimensions(originalWidth, originalHeight, maxWidth, maxHeight) {
  let width = originalWidth;
  let height = originalHeight;
  let resized = false;

  if (width > maxWidth || height > maxHeight) {
    resized = true;
    const aspectRatio = width / height;

    if (width > maxWidth) {
      width = maxWidth;
      height = width / aspectRatio;
    }

    if (height > maxHeight) {
      height = maxHeight;
      width = height * aspectRatio;
    }
  }

  return {
    width: Math.round(width),
    height: Math.round(height),
    resized,
  };
}

function calculateCoverCrop(originalWidth, originalHeight, targetWidth, targetHeight) {
  const sourceAspectRatio = originalWidth / originalHeight;
  const targetAspectRatio = targetWidth / targetHeight;

  let sourceX = 0;
  let sourceY = 0;
  let sourceWidth = originalWidth;
  let sourceHeight = originalHeight;

  if (sourceAspectRatio > targetAspectRatio) {
    sourceWidth = originalHeight * targetAspectRatio;
    sourceX = (originalWidth - sourceWidth) / 2;
  } else if (sourceAspectRatio < targetAspectRatio) {
    sourceHeight = originalWidth / targetAspectRatio;
    sourceY = (originalHeight - sourceHeight) / 2;
  }

  return {
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    targetWidth,
    targetHeight,
  };
}

async function createCompressedFile(canvas, file, config) {
  const {
    maxFileSize,
    initialQuality,
    minQuality,
    qualityStep,
    outputMimeWebp,
    outputMimeJpeg,
  } = config;

  let quality = initialQuality;
  let compressedFile = null;
  let attempts = 0;
  const maxAttempts = Math.ceil((initialQuality - minQuality) / qualityStep) + 1;
  let outputMime = outputMimeWebp;

  while (attempts < maxAttempts) {
    let blob = await new Promise((resolveBlob) => {
      canvas.toBlob(resolveBlob, outputMime, quality);
    });

    if (!blob && outputMime === outputMimeWebp) {
      outputMime = outputMimeJpeg;
      blob = await new Promise((resolveBlob) => {
        canvas.toBlob(resolveBlob, outputMime, quality);
      });
    }

    if (!blob) {
      throw new Error("이미지 압축에 실패했습니다.");
    }

    const originalNameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
    const fileExtension = outputMime === outputMimeWebp ? "webp" : "jpg";
    const newFileName = `${originalNameWithoutExt}.${fileExtension}`;

    compressedFile = new File([blob], newFileName, {
      type: outputMime,
      lastModified: Date.now(),
    });

    if (compressedFile.size <= maxFileSize) {
      break;
    }

    quality -= qualityStep;
    attempts++;

    if (quality < minQuality) {
      console.warn(`10MB 이하로 압축할 수 없습니다. 최종 크기: ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
      break;
    }
  }

  return compressedFile;
}

async function compressImageWithConfig(file, customConfig = {}) {
  const config = {
    ...DEFAULT_COMPRESS_CONFIG,
    ...customConfig,
  };

  if (!file || !file.type || !file.type.startsWith("image/")) {
    return file;
  }

  const img = await loadImageElement(file);
  const originalWidth = img.width;
  const originalHeight = img.height;

  let canvasWidth = originalWidth;
  let canvasHeight = originalHeight;
  let needResize = false;
  let drawArgs = null;

  if (config.mode === "cover" && config.targetWidth && config.targetHeight) {
    canvasWidth = config.targetWidth;
    canvasHeight = config.targetHeight;
    needResize = originalWidth !== config.targetWidth || originalHeight !== config.targetHeight;
    drawArgs = calculateCoverCrop(
      originalWidth,
      originalHeight,
      config.targetWidth,
      config.targetHeight
    );
  } else {
    const contained = calculateContainDimensions(
      originalWidth,
      originalHeight,
      config.maxWidth,
      config.maxHeight
    );

    canvasWidth = contained.width;
    canvasHeight = contained.height;
    needResize = contained.resized;
  }

  const canvas = document.createElement("canvas");
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("캔버스를 초기화할 수 없습니다.");
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.fillStyle = config.backgroundColor;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  if (drawArgs) {
    ctx.drawImage(
      img,
      drawArgs.sourceX,
      drawArgs.sourceY,
      drawArgs.sourceWidth,
      drawArgs.sourceHeight,
      0,
      0,
      canvas.width,
      canvas.height
    );
  } else {
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  }

  const compressedFile = await createCompressedFile(canvas, file, config);

  if (compressedFile.size >= file.size && !needResize && file.size <= config.maxFileSize) {
    return file;
  }

  if (compressedFile.size > config.maxFileSize) {
    console.warn("⚠️ 압축 후에도 10MB를 초과합니다. 더 작은 이미지를 사용하세요.");
  }

  if (process.env.NODE_ENV !== "production") {
    try {
      // eslint-disable-next-line no-console
      console.groupCollapsed(`[compressImage] ${config.debugLabel}`);
      // eslint-disable-next-line no-console
      console.log("원본 파일:", {
        name: file.name,
        type: file.type,
        size: formatFileSize(file.size),
        width: originalWidth,
        height: originalHeight,
      });
      // eslint-disable-next-line no-console
      console.log("압축 결과:", {
        name: compressedFile.name,
        type: compressedFile.type,
        size: formatFileSize(compressedFile.size),
        width: canvas.width,
        height: canvas.height,
        mode: config.mode,
      });
      // eslint-disable-next-line no-console
      console.groupEnd();
    } catch (logError) {
      // 로깅 중 에러는 무시
    }
  }

  return compressedFile;
}

/**
 * 이미지 파일을 압축하고 리사이즈합니다.
 * 10MB를 초과하면 자동으로 품질을 낮춰 재압축합니다.
 *
 * @param {File} file - 원본 이미지 파일
 * @returns {Promise<File>} 압축된 이미지 파일
 */
export async function compressImage(file) {
  return compressImageWithConfig(file);
}

/**
 * 배너 이미지를 1450x485로 크롭/리사이즈한 뒤 압축합니다.
 *
 * @param {File} file - 원본 배너 파일
 * @returns {Promise<File>} 1450x485로 보정된 배너 파일
 */
export async function compressBannerImage(file) {
  return compressImageWithConfig(file, BANNER_COMPRESS_CONFIG);
}

/**
 * 이미지 파일인지 확인합니다.
 */
export function isImageFile(file) {
  return file && file.type && file.type.startsWith('image/');
}

/**
 * 파일 크기를 사람이 읽기 쉬운 형식으로 변환합니다.
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}
